import axios from 'axios';
import { useCallback, useEffect, useState } from 'react'
import { CountryCodeEnum } from '../.mesh';

type CustomerAddress = {
  countryCode?: CountryCodeEnum | string | null
  street?: String
  houseNumber: String
  postcode?: String | null
  regionCode?: String | null
  city?: String | null
}

type GoogleAddress = {
  regionCode?: CountryCodeEnum | string | null
  addressLines?: [String | undefined, String | undefined]
  postalCode?: String | null
  administrativeArea?: String | null
  locality?: String | null
}

const useGoogleAddressValidation = () => {
  const publicKey = import.meta.graphCommerce.googleMapsApiKey;

  const validateCustomerAddress = useCallback((address: CustomerAddress) => {
    const result = fetch({
      regionCode: address.countryCode,
      addressLines: [address.street, address.houseNumber],
      postalCode: address.postcode,
      administrativeArea: address.regionCode,
      locality: address.city,
    });
    return result;
  }, [])

  const fetch = useCallback(async (googleAddress: GoogleAddress) => {
    const url = `https://addressvalidation.googleapis.com/v1:validateAddress?key=${publicKey}`;
    const { data } = await axios.post(url, {
      address: googleAddress
    })

    let invalidFields = [];
    if (data) {
      if (Array.isArray(data.result.address.unconfirmedComponentTypes)) {
        invalidFields = data.result.address.unconfirmedComponentTypes;
      }
    }
    return {
      plainResult: data,
      invalidFields,
      hasUnconfirmedComponents: data.result.hasUnconfirmedComponents
    };
  }, [publicKey])

  const processFormBasedOnResult = useCallback((result, form, formData) => {
    let isValid = true;
    let formDataProcessed = { ...formData };

    const addressComponents = result.plainResult.result.address.addressComponents;

    const postCodeComp = addressComponents.find(comp => comp.componentType == 'postal_code');
    if (postCodeComp) {
      if (postCodeComp.confirmationLevel == 'CONFIRMED') {
        if (postCodeComp.replaced) {
          form.setValue('postcode', postCodeComp.componentName.text)
          formDataProcessed = { ...formDataProcessed, city: postCodeComp.componentName.text }
        }
      } else {
        form.setError('postcode', { message: 'Wrong Postcode' })
        isValid = false;
      }
    }

    const cityComp = addressComponents.find(comp => comp.componentType == 'locality');
    if (cityComp) {
      if (cityComp.confirmationLevel == 'CONFIRMED') {
        form.setValue('city', cityComp.componentName.text)
        formDataProcessed = { ...formDataProcessed, city: cityComp.componentName.text }
      } else {
        form.setError('city', { message: 'Wrong City' })
        isValid = false;
      }
    }

    const streetComp = addressComponents.find(comp => comp.componentType == 'route');
    const streetNumberComp = addressComponents.find(comp => comp.componentType == 'street_number');
    if (streetComp && streetNumberComp) {
      if (streetComp.confirmationLevel == 'CONFIRMED') {
        form.setValue('street', `${streetNumberComp.componentName.text} ${streetComp.componentName.text}`)
        formDataProcessed = { ...formDataProcessed, street: `${streetNumberComp.componentName.text} ${streetComp.componentName.text}` }
      } else {
        form.setError('street', { message: 'Wrong Street' })
        isValid = false;
      }
    }

    return {
      isValid,
      formDataProcessed
    };
  }, [])

  return {
    validateCustomerAddress,
    processFormBasedOnResult
  }
}
export { useGoogleAddressValidation }
